import React, { useState } from 'react';
import { ArrowLeft, X } from 'lucide-react';
import { MainLayout } from './MainLayout';
import { useApp } from './AppContext';
import { useTranslation } from '../i18n/TranslationContext';

interface IDData {
  personalausweisnummer: string;
  vorname_idcard: string;
  nachname: string;
  geburtsdatum: string;
  geburtsort: string;
  validierung_erfolgreich: boolean;
}

export function IDTerms() {
  const { setCurrentScreen, idCheckState } = useApp();
  const { t } = useTranslation();
  
  const [idData, setIdData] = useState<IDData>({
    personalausweisnummer: '',
    vorname_idcard: '',
    nachname: '',
    geburtsdatum: '',
    geburtsort: '',
    validierung_erfolgreich: false
  });

  const [errors, setErrors] = useState<Partial<IDData>>({});

  const handleBack = () => {
    setCurrentScreen('dashboard');
  };

  const handleClose = () => {
    setCurrentScreen('dashboard');
  };

  const handleInputChange = (field: keyof IDData, value: string | boolean) => {
    setIdData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const validateForm = () => {
    const newErrors: Partial<IDData> = {};
    
    if (!idData.personalausweisnummer.trim()) {
      newErrors.personalausweisnummer = t('idTerms.validation.idNumberRequired');
    }
    
    if (!idData.vorname_idcard.trim()) {
      newErrors.vorname_idcard = t('idTerms.validation.firstNameRequired');
    }
    
    if (!idData.nachname.trim()) {
      newErrors.nachname = t('idTerms.validation.lastNameRequired');
    }
    
    if (!idData.geburtsdatum) {
      newErrors.geburtsdatum = t('idTerms.validation.dateOfBirthRequired');
    }
    
    if (!idData.geburtsort.trim()) {
      newErrors.geburtsort = t('idTerms.validation.placeOfBirthRequired');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    try {
      // Simulate API call
      console.log('Submitting ID data:', idData);
      
      // Mark validation as successful
      setIdData(prev => ({
        ...prev,
        validierung_erfolgreich: true
      }));
      
      // Navigate to ID capture after successful validation
      setTimeout(() => {
        setCurrentScreen('id-capture');
      }, 2000);
    } catch (error) {
      console.error('ID validation failed:', error);
    }
  };

  const handleCancel = () => {
    setCurrentScreen('dashboard');
  };

  return (
    <MainLayout showBack={false} showBottomNav={false}>
      {/* Custom Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={handleBack}
          className="w-10 h-10 bg-[var(--ucm-disabled-gray)] rounded-full flex items-center justify-center hover:bg-[var(--ucm-border-medium)] transition-colors duration-200"
        >
          <ArrowLeft className="w-5 h-5 text-[var(--ucm-text-dark)]" />
        </button>
        
        <button
          onClick={handleClose}
          className="w-10 h-10 flex items-center justify-center hover:bg-[var(--ucm-disabled-gray)] rounded-full transition-colors duration-200"
        >
          <X className="w-5 h-5 text-[var(--ucm-text-dark)]" />
        </button>
      </div>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[var(--ucm-text-dark)]">
          {t('idTerms.title')}
        </h1>
      </div>

      <div className="bg-white rounded-2xl p-6 shadow-[0_4px_16px_rgba(0,0,0,0.08)] mb-6">
        <h3 className="font-semibold text-[var(--ucm-text-dark)] mb-6">
          {t('idTerms.personalIdData')}
        </h3>
        
        <div className="space-y-5">
          <div className="form-group">
            <label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
              {t('idTerms.idNumber')} *
            </label>
            <input
              type="text"
              className={`w-full p-3 border rounded-lg text-base bg-white transition-colors duration-200 ${
                errors.personalausweisnummer 
                  ? 'border-[var(--ucm-error-red)] focus:border-[var(--ucm-error-red)]' 
                  : 'border-[var(--ucm-border-light)] focus:border-[var(--ucm-info-blue)]'
              } focus:outline-none focus:ring-2 focus:ring-opacity-20 ${
                errors.personalausweisnummer 
                  ? 'focus:ring-[var(--ucm-error-red)]' 
                  : 'focus:ring-[var(--ucm-info-blue)]'
              }`}
              value={idData.personalausweisnummer}
              onChange={(e) => handleInputChange('personalausweisnummer', e.target.value)}
              placeholder={t('idTerms.idNumberPlaceholder')}
              maxLength={10}
              disabled={idData.validierung_erfolgreich}
            />
            {errors.personalausweisnummer && (
              <span className="block mt-1 text-xs text-[var(--ucm-error-red)]">
                {errors.personalausweisnummer}
              </span>
            )}
          </div>
          
          <div className="form-group">
            <label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
              {t('idTerms.firstName')} *
            </label>
            <input
              type="text"
              className={`w-full p-3 border rounded-lg text-base bg-white transition-colors duration-200 ${
                errors.vorname_idcard 
                  ? 'border-[var(--ucm-error-red)] focus:border-[var(--ucm-error-red)]' 
                  : 'border-[var(--ucm-border-light)] focus:border-[var(--ucm-info-blue)]'
              } focus:outline-none focus:ring-2 focus:ring-opacity-20 ${
                errors.vorname_idcard 
                  ? 'focus:ring-[var(--ucm-error-red)]' 
                  : 'focus:ring-[var(--ucm-info-blue)]'
              }`}
              value={idData.vorname_idcard}
              onChange={(e) => handleInputChange('vorname_idcard', e.target.value)}
              placeholder={t('idTerms.firstNamePlaceholder')}
              disabled={idData.validierung_erfolgreich}
            />
            {errors.vorname_idcard && (
              <span className="block mt-1 text-xs text-[var(--ucm-error-red)]">
                {errors.vorname_idcard}
              </span>
            )}
          </div>
          
          <div className="form-group">
            <label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
              {t('idTerms.lastName')} *
            </label>
            <input
              type="text"
              className={`w-full p-3 border rounded-lg text-base bg-white transition-colors duration-200 ${
                errors.nachname 
                  ? 'border-[var(--ucm-error-red)] focus:border-[var(--ucm-error-red)]' 
                  : 'border-[var(--ucm-border-light)] focus:border-[var(--ucm-info-blue)]'
              } focus:outline-none focus:ring-2 focus:ring-opacity-20 ${
                errors.nachname 
                  ? 'focus:ring-[var(--ucm-error-red)]' 
                  : 'focus:ring-[var(--ucm-info-blue)]'
              }`}
              value={idData.nachname}
              onChange={(e) => handleInputChange('nachname', e.target.value)}
              placeholder={t('idTerms.lastNamePlaceholder')}
              disabled={idData.validierung_erfolgreich}
            />
            {errors.nachname && (
              <span className="block mt-1 text-xs text-[var(--ucm-error-red)]">
                {errors.nachname}
              </span>
            )}
          </div>
          
          <div className="form-group">
            <label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
              {t('idTerms.dateOfBirth')} *
            </label>
            <input
              type="date"
              className={`w-full p-3 border rounded-lg text-base bg-white transition-colors duration-200 ${
                errors.geburtsdatum 
                  ? 'border-[var(--ucm-error-red)] focus:border-[var(--ucm-error-red)]' 
                  : 'border-[var(--ucm-border-light)] focus:border-[var(--ucm-info-blue)]'
              } focus:outline-none focus:ring-2 focus:ring-opacity-20 ${
                errors.geburtsdatum 
                  ? 'focus:ring-[var(--ucm-error-red)]' 
                  : 'focus:ring-[var(--ucm-info-blue)]'
              }`}
              value={idData.geburtsdatum}
              onChange={(e) => handleInputChange('geburtsdatum', e.target.value)}
              disabled={idData.validierung_erfolgreich}
            />
            {errors.geburtsdatum && (
              <span className="block mt-1 text-xs text-[var(--ucm-error-red)]">
                {errors.geburtsdatum}
              </span>
            )}
          </div>
          
          <div className="form-group">
            <label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
              {t('idTerms.placeOfBirth')} *
            </label>
            <input
              type="text"
              className={`w-full p-3 border rounded-lg text-base bg-white transition-colors duration-200 ${
                errors.geburtsort 
                  ? 'border-[var(--ucm-error-red)] focus:border-[var(--ucm-error-red)]' 
                  : 'border-[var(--ucm-border-light)] focus:border-[var(--ucm-info-blue)]'
              } focus:outline-none focus:ring-2 focus:ring-opacity-20 ${
                errors.geburtsort 
                  ? 'focus:ring-[var(--ucm-error-red)]' 
                  : 'focus:ring-[var(--ucm-info-blue)]'
              }`}
              value={idData.geburtsort}
              onChange={(e) => handleInputChange('geburtsort', e.target.value)}
              placeholder={t('idTerms.placeOfBirthPlaceholder')}
              disabled={idData.validierung_erfolgreich}
            />
            {errors.geburtsort && (
              <span className="block mt-1 text-xs text-[var(--ucm-error-red)]">
                {errors.geburtsort}
              </span>
            )}
          </div>
        </div>
        
        {/* Validation Success Indicator */}
        {idData.validierung_erfolgreich && (
          <div className="mt-6 bg-[var(--ucm-light-green)] border border-[var(--ucm-success-green)] rounded-xl p-5 text-center">
            <div className="text-3xl mb-3">✅</div>
            <h3 className="font-semibold text-[var(--ucm-dark-green)] mb-2">
              {t('idTerms.validationSuccessful')}
            </h3>
            <p className="text-sm text-[var(--ucm-dark-green)] mb-0">
              {t('idTerms.validationSuccessMessage')}
            </p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="space-y-3">
        <button
          onClick={handleSubmit}
          disabled={idData.validierung_erfolgreich}
          className={`w-full h-14 font-semibold rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
            idData.validierung_erfolgreich
              ? 'bg-[var(--ucm-success-green)] text-white cursor-not-allowed'
              : 'bg-[var(--ucm-primary-teal)] text-white hover:bg-[var(--ucm-primary-teal-hover)] focus:ring-[var(--ucm-primary-teal)]'
          }`}
        >
          {idData.validierung_erfolgreich ? t('idTerms.validated') : t('idTerms.validateData')}
        </button>
        
        <button
          onClick={handleCancel}
          className="w-full h-14 bg-[var(--ucm-disabled-gray)] text-[var(--ucm-text-dark)] font-semibold rounded-full hover:bg-[var(--ucm-border-medium)] transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-border-medium)] focus:ring-offset-2"
        >
          {t('idTerms.cancel')}
        </button>
      </div>
    </MainLayout>
  );
}